import { useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { Check, Zap, Bitcoin, CreditCard, KeyRound } from 'lucide-react';
import { AppLayout } from '@/components/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  useSubscription,
  PRICING,
  YEARLY_SAVINGS_PCT,
  type BillingPlan,
} from '@/hooks/useSubscription';
import { BchPaymentDialog } from '@/components/BchPaymentDialog';
import {
  isValidLicense,
  setStoredLicense,
  clearStoredLicense,
} from '@/lib/license';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/utils';

const FREE_FEATURES = [
  'Global Liquidity score',
  'BCH Setup score',
  'Basic indicators (6 per domain)',
  'Daily brief',
  'Limited history',
  'Limited alerts',
];

const PRO_FEATURES = [
  'Full indicator set',
  'Faster alerts where data allows',
  'Full historical data',
  'BCH rotation alerts',
  'Advanced daily brief',
  'Push notifications',
  'Custom alerts & thresholds',
  'Liquidity-cycle history',
  'Advanced signal explanations',
];

const Pricing = () => {
  useSeoMeta({
    title: 'Pricing — BCH Radar',
    description: 'Free and Pro plans. Pay with a card or non-custodially with Bitcoin Cash.',
  });

  const {
    isPro,
    isOwner,
    hasLicense,
    claimPayment,
    expiresAt,
    method,
    plan: activePlan,
  } = useSubscription();
  const { toast } = useToast();
  const [bchOpen, setBchOpen] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreKey, setRestoreKey] = useState('');
  const [plan, setPlan] = useState<BillingPlan>('yearly');

  const handleRestore = () => {
    const key = restoreKey.trim().toUpperCase();
    if (!isValidLicense(key)) {
      toast({
        title: 'That key doesn’t look right',
        description:
          'A license key looks like BCHR-XXXX-XXXX-XXXX-XXXX-XXXX. Please check and try again.',
        variant: 'destructive',
      });
      return;
    }
    setStoredLicense(key);
    setRestoreOpen(false);
    setRestoreKey('');
    toast({
      title: 'License applied',
      description: 'Checking your Pro status…',
    });
  };

  const proPrice = plan === 'yearly' ? PRICING.proYearlyUsd : PRICING.proMonthlyUsd;
  const perMonthEquivalent =
    plan === 'yearly' ? (PRICING.proYearlyUsd / 12).toFixed(2) : null;

  return (
    <AppLayout>
      <div className="mx-auto max-w-3xl text-center">
        <h1 className="text-3xl font-bold tracking-tight">Choose your plan</h1>
        <p className="mt-2 text-muted-foreground">
          Serious macro intelligence for BCH investors and builders. Change or
          cancel anytime.
        </p>
      </div>

      {/* Billing period toggle */}
      {!isPro && (
        <div className="mx-auto mt-6 flex w-full max-w-xs items-center rounded-full border border-border/60 bg-secondary/40 p-1">
          <button
            onClick={() => setPlan('monthly')}
            className={cn(
              'flex-1 rounded-full px-4 py-1.5 text-sm font-medium transition-colors',
              plan === 'monthly'
                ? 'bg-primary text-primary-foreground shadow'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            Monthly
          </button>
          <button
            onClick={() => setPlan('yearly')}
            className={cn(
              'flex-1 rounded-full px-4 py-1.5 text-sm font-medium transition-colors',
              plan === 'yearly'
                ? 'bg-primary text-primary-foreground shadow'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            Yearly
            <span
              className={cn(
                'ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold',
                plan === 'yearly'
                  ? 'bg-primary-foreground/20 text-primary-foreground'
                  : 'bg-primary/15 text-primary',
              )}
            >
              −{YEARLY_SAVINGS_PCT}%
            </span>
          </button>
        </div>
      )}

      <div className="mx-auto mt-8 grid max-w-3xl gap-4 md:grid-cols-2">
        {/* Free */}
        <Card className={cn(!isPro && 'ring-1 ring-border')}>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Free
              {!isPro && <Badge variant="secondary">Current</Badge>}
            </CardTitle>
            <p className="text-3xl font-bold">
              $0
              <span className="text-sm font-normal text-muted-foreground"> / mo</span>
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <ul className="space-y-2 text-sm">
              {FREE_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  {f}
                </li>
              ))}
            </ul>
            {isPro && !isOwner && (
              <p className="text-xs text-muted-foreground">
                Your Pro access stays active until it expires. It is tied to
                your Nostr account — no auto-renewal, nothing to cancel.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Pro */}
        <Card className={cn('relative border-primary/40', isPro && 'ring-1 ring-primary')}>
          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
            <Badge className="gap-1 bg-primary text-primary-foreground">
              <Zap className="h-3 w-3" /> Most popular
            </Badge>
          </div>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Pro
              {isPro && <Badge className="bg-primary/15 text-primary border-primary/30">Active</Badge>}
            </CardTitle>
            <p className="text-3xl font-bold">
              ${proPrice}
              <span className="text-sm font-normal text-muted-foreground">
                {' '}
                / {plan === 'yearly' ? 'yr' : 'mo'}
              </span>
            </p>
            {perMonthEquivalent && (
              <p className="text-xs text-primary">
                ${perMonthEquivalent}/mo billed annually · one lump BCH payment
              </p>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <ul className="space-y-2 text-sm">
              {PRO_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  {f}
                </li>
              ))}
            </ul>

            {isOwner ? (
              <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-center text-sm">
                <p className="font-medium text-primary">Owner — Pro included ⭐</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Your account is the project owner, so Pro is always on.
                </p>
              </div>
            ) : isPro ? (
              <div className="space-y-2">
                <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-center text-sm">
                  <p className="font-medium text-primary">Pro is active 🎉</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {activePlan === 'yearly' ? 'Yearly' : 'Monthly'} · paid with{' '}
                    {method === 'bch' ? 'Bitcoin Cash' : 'subscription'} · expires{' '}
                    {expiresAt ? new Date(expiresAt).toLocaleDateString() : '—'}
                  </p>
                </div>
                {hasLicense && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full text-xs text-muted-foreground"
                    onClick={() => {
                      clearStoredLicense();
                      toast({
                        title: 'License removed from this device',
                        description:
                          'Pro is no longer active here. Re-enter your key anytime to restore it.',
                      });
                    }}
                  >
                    Remove license from this device
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <Button
                  size="lg"
                  className="w-full gap-2 text-base font-semibold shadow-lg shadow-primary/20"
                  onClick={() => setBchOpen(true)}
                >
                  <Bitcoin className="h-4 w-4" /> Pay with BCH ·{' '}
                  {plan === 'yearly' ? 'Yearly' : 'Monthly'}
                </Button>
                <Button
                  variant="outline"
                  className="w-full gap-2"
                  disabled
                >
                  <CreditCard className="h-4 w-4" /> Card — coming soon
                </Button>
                <p className="text-center text-[11px] text-muted-foreground">
                  No account needed. Scan, pay with Bitcoin Cash, and Pro
                  unlocks automatically — you&apos;ll get a license key to keep.
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full gap-2 text-xs text-muted-foreground"
                  onClick={() => setRestoreOpen(true)}
                >
                  <KeyRound className="h-3.5 w-3.5" /> Already paid? Restore with
                  a license key
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <p className="mx-auto mt-6 max-w-2xl text-center text-xs text-muted-foreground">
        BCH payments are non-custodial: you pay a fixed address and the app
        verifies the transaction. Additional payment methods can be added
        later — pricing is centralized so it can change without a rebuild.
      </p>

      <BchPaymentDialog
        open={bchOpen}
        onOpenChange={setBchOpen}
        plan={plan}
        onPaid={async ({ amount, since }) => {
          try {
            // The server re-verifies on-chain and mints a license key. The
            // dialog shows the key on success; we keep it open so the user can
            // copy it before closing.
            const { license } = await claimPayment({ amount, since, plan });
            toast({
              title: 'Payment confirmed on-chain',
              description: `Pro unlocked (${plan === 'yearly' ? 'yearly' : 'monthly'}). Thanks for supporting P2P cash!`,
            });
            return { license };
          } catch {
            toast({
              title: 'Payment detected — finalizing',
              description:
                'We saw your payment but could not confirm Pro yet. It will activate shortly; please keep this tab open or refresh in a moment.',
              variant: 'destructive',
            });
          }
        }}
      />

      {/* Restore Pro with a previously-issued license key. */}
      <Dialog open={restoreOpen} onOpenChange={setRestoreOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Restore Pro</DialogTitle>
            <DialogDescription>
              Enter the license key you received after paying. It unlocks Pro on
              this device — no account required.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={restoreKey}
            onChange={(e) => setRestoreKey(e.target.value)}
            placeholder="BCHR-XXXX-XXXX-XXXX-XXXX-XXXX"
            autoCapitalize="characters"
            spellCheck={false}
            className="font-mono"
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleRestore();
            }}
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRestoreOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleRestore} className="gap-2">
              <KeyRound className="h-4 w-4" /> Restore Pro
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
};

export default Pricing;
